// Документы — the card's fifth pane (BLOGGER-CARD-STRUCT 2026-08-19, spec §6.7 / F5).
//
// P5 fills the P2 stub. This is LeadSidePanel's «Договоры» section minus OverviewSection:
// the same query key (`queryKeys.contracts.byLead`), the same two actions with the same two
// invalidations, the same RU copy — and `ContractCard` mounted UNCHANGED (§6.7, apply 7).
// The one substantive difference from the panel is the query: the server-side `leadId`
// filter of §5.2 replaces `fetchContracts().then(all => all.filter(...))`
// (LeadSidePanel.jsx:20), so this surface never pulls the 856-row global contract list to
// render one lead's contracts.
//
// TWO HOSTS mount this component (D1 option C, owner ruling 2026-08-19): the card's
// Документы pane, and the thin `AccountantDocsOverlay` on /documents + /invoices, which
// feeds it the registry's ALREADY-COMPOSED lead object — no `/api/leads/:id` read happens
// for a role that is a hard 403 on it (§5.4). Nothing here branches on role: both hosts
// pass a lead and get the same surface.
//
// The query is gated on the pane being active (the JournalTab idiom, LeadDetailsJournalTab.jsx:39),
// so opening the card on Инфо reaches no contracts endpoint at all — AC-30.
//
// F10: one purple CTA on the surface — «+ Новый договор» is the accent, «Прямая выплата»
// is secondary, exactly as the panel had them.
import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchContracts } from '../api/contracts.js';
import { queryKeys } from '../lib/queryKeys';
import { Button, EmptyState } from './ds';
import ContractCard from './ContractCard';
import CreateContractInlineForm from './CreateContractInlineForm';
import DirectPaymentModal from './DirectPaymentModal.jsx';
import LeadCabinetSection from './LeadCabinetSection.jsx';

export function LeadDetailsDocsTab ({ panelTab, lead }) {
    const isActive = panelTab === 'docs';
    const qc = useQueryClient();
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isDirectOpen, setIsDirectOpen] = useState(false);
    const leadId = lead?.id ?? null;

    const { data: allContracts = [], isLoading } = useQuery({
        queryKey: queryKeys.contracts.byLead(leadId ?? 'none'),
        queryFn: () => fetchContracts({ leadId }),
        enabled: isActive && Boolean(leadId),
        staleTime: 30_000,
    });

    // Carried from the panel (LeadSidePanel.jsx:27-29): newest first.
    const contracts = useMemo(
        () => [...allContracts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
        [allContracts],
    );

    // The panel's two invalidations, unchanged (LeadSidePanel.jsx:72-73).
    const invalidateContracts = () => {
        qc.invalidateQueries({ queryKey: queryKeys.contracts.byLead(leadId) });
        qc.invalidateQueries({ queryKey: queryKeys.contracts.all });
    };

    return (
        <section
            className='details-modal__section oc-lead-card__docs'
            style={{ display: isActive ? 'flex' : 'none' }}
        >
            <div className='oc-lead-card__docs-head'>
                <span className='oc-lead-card__docs-title'>
                    Договоры
                    {contracts.length > 0 && (
                        <span className='oc-lead-card__docs-count'> · {contracts.length}</span>
                    )}
                </span>
                <span className='oc-lead-card__docs-actions'>
                    {!isCreateOpen && (
                        <Button hierarchy='primary' size='small' onClick={() => setIsCreateOpen(true)}>
                            + Новый договор
                        </Button>
                    )}
                    {!isDirectOpen && (
                        <Button
                            hierarchy='secondary'
                            size='small'
                            title='Выплата без договора — фиксация суммы и реквизитов'
                            onClick={() => setIsDirectOpen(true)}
                        >
                            Прямая выплата
                        </Button>
                    )}
                </span>
            </div>

            {isCreateOpen && (
                <CreateContractInlineForm
                    lead={lead}
                    onCancel={() => setIsCreateOpen(false)}
                    onCreated={() => {
                        setIsCreateOpen(false);
                        invalidateContracts();
                    }}
                />
            )}

            {isDirectOpen && (
                <DirectPaymentModal lead={lead} onClose={() => setIsDirectOpen(false)} />
            )}

            {isLoading ? (
                <p className='oc-lead-card__docs-note'>Загрузка…</p>
            ) : contracts.length === 0 ? (
                !isCreateOpen && (
                    <EmptyState>
                        <span className='oc-lead-card__empty-title'>Договоры</span>
                        <span className='oc-lead-card__empty-desc'>У этого лида пока нет договоров.</span>
                    </EmptyState>
                )
            ) : (
                <div className='contract-cards-list'>
                    {contracts.map((c) => (
                        <ContractCard key={c.id} contract={c} lead={lead} />
                    ))}
                </div>
            )}

            <LeadCabinetSection leadId={leadId} enabled={isActive} />
        </section>
    );
}
